// noinspection DuplicatedCode

import color from 'color';
import { DefaultTheme } from 'styled-components';

/* eslint-disable @typescript-eslint/no-unused-vars */
// Background colors
const colorBackground = color.rgb(255, 255, 255);
const colorElevation0 = colorBackground.darken(0.02);
const colorElevation1 = colorBackground.darken(0.035);
const colorElevation2 = colorBackground.darken(0.05);
const colorElevation3 = colorBackground.darken(0.07);
const colorElevation4 = colorBackground.darken(0.09);
const colorElevation5 = colorBackground.darken(0.12);
// const colorElevation6 = colorBackground.darken(0.15);
// const colorElevation7 = colorBackground.darken(0.18);
const colorElevation8 = colorBackground.darken(0.21);
const colorElevation9 = colorBackground.darken(0.24);

// Text colors
const colorText = color.rgb(0, 0, 0);
const colorEmphasisHigh = colorText.alpha(0.87);
const colorEmphasisMedium = colorText.alpha(0.54);
const colorEmphasisLow = colorText.alpha(0.3);
const colorInverted = color.rgb(255, 255, 255);

// Miscellaneous colors
const colorHighlight = color.rgb(255, 255, 0);
const colorInfo = color.rgb(94, 96, 206);
const colorNegative = color.rgb(191, 6, 3);
const colorPositive = color.rgb(144, 169, 85);
const colorPrimary = color.rgb(0, 119, 182);
const colorWarning = color.rgb(247, 127, 0);

// Transaction type colors
const colorTxBank = color.rgb(82, 183, 136);
const colorTxState = color.rgb(188, 184, 177);
const colorTxSystem = color.rgb(0, 162, 215);

// Transaction state colors
const colorTxIgnored = color.rgb(244, 140, 6);
const colorTxMatched = color.rgb(171, 184, 84);
const colorTxUnmatched = colorEmphasisLow;

// Transaction action and highlight colors
const colorTxDeselect = color.rgb(205, 180, 219);
const colorTxHighlight = color.rgb(255, 109, 0);
const colorTxIgnore = colorTxIgnored;
const colorTxMatch = colorTxMatched;
const colorTxRestore = color.rgb(0, 180, 216);
const colorTxSuccess = colorPositive;
const colorTxUnmatch = color.rgb(255, 0, 0).lighten(0.2);
/* eslint-enable @typescript-eslint/no-unused-vars */

export const light: DefaultTheme = {
  body: {
    background: colorElevation1.toString(),
    color: colorEmphasisHigh.toString(),
  },
  brand: {
    main: colorPrimary.toString(),
  },
  breadcrumbs: {
    border: colorElevation5.toString(),
    chevron: colorEmphasisMedium.toString(),
    highlight: colorPrimary.alpha(0.5).toString(),
    icon: colorEmphasisMedium.toString(),
  },
  button: {
    default: {
      background: colorElevation4.toString(),
      color: colorEmphasisHigh.toString(),
      disabled: {
        background: colorElevation4.alpha(0.3).toString(),
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorElevation8.toString(),
      },
    },
    negative: {
      background: colorNegative.toString(),
      color: colorInverted.toString(),
      disabled: {
        background: colorNegative.alpha(0.3).toString(),
        color: colorInverted.toString(),
      },
      hover: {
        background: colorNegative.darken(0.15).toString(),
      },
    },
    positive: {
      background: colorPositive.toString(),
      color: colorInverted.toString(),
      disabled: {
        background: colorPositive.alpha(0.3).toString(),
        color: colorInverted.toString(),
      },
      hover: {
        background: colorPositive.darken(0.15).toString(),
      },
    },
    primary: {
      background: colorPrimary.toString(),
      color: colorInverted.toString(),
      disabled: {
        background: colorPrimary.alpha(0.3).toString(),
        color: colorInverted.toString(),
      },
      hover: {
        background: colorPrimary.darken(0.15).toString(),
      },
    },
    transparent: {
      background: 'transparent',
      color: colorEmphasisHigh.toString(),
      disabled: {
        background: 'transparent',
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorElevation3.toString(),
      },
    },
  },
  card: {
    actions: {
      background: colorElevation1.toString(),
    },
    background: colorBackground.toString(),
    header: {
      border: colorElevation2.toString(),
    },
    meta: {
      color: colorEmphasisLow.toString(),
    },
    progress: {
      background: colorElevation0.toString(),
      color: colorPrimary.toString(),
    },
    status: {
      background: colorElevation1.toString(),
      color: colorEmphasisMedium.toString(),
      item: {
        background: 'transparent',
        hover: {
          background: colorElevation3.toString(),
        },
      },
    },
  },
  datePicker: {
    calendar: {
      button: {
        color: colorEmphasisHigh.toString(),
        disabled: {
          background: colorElevation3.toString(),
          color: colorEmphasisLow.toString(),
        },
        hover: {
          background: colorElevation3.toString(),
        },
      },
      day: {
        hover: {
          border: colorElevation9.toString(),
        },
      },
      neighboringMonth: {
        color: colorEmphasisLow.toString(),
      },
      selected: {
        background: colorPrimary.toString(),
        border: colorPrimary.toString(),
        color: colorInverted.toString(),
      },
      today: {
        border: colorPrimary.toString(),
        color: colorPrimary.toString(),
      },
      weekend: {
        color: colorNegative.lighten(0.5).toString(),
      },
    },
    icon: {
      color: colorEmphasisLow.toString(),
      hover: colorEmphasisMedium.toString(),
    },
    input: {
      invalid: {
        background: colorNegative.alpha(0.3).toString(),
      },
    },
  },
  element: {
    code: {
      background: colorElevation2.toString(),
    },
    kbd: {
      background: colorElevation2.toString(),
      shadow: '0 0.25em 0 0 rgba(0, 0, 0, 0.18)',
    },
    height: 42,
    lineHeight: '1.4em',
  },
  form: {
    error: colorNegative.toString(),
  },
  fullscreen: {
    background: colorElevation3.toString(),
    panel: {
      background: colorBackground.toString(),
    },
  },
  highlight: {
    background: colorHighlight.toString(),
    color: colorText.toString(),
  },
  input: {
    background: colorElevation0.toString(),
    border: colorElevation8.toString(),
    color: colorEmphasisHigh.toString(),
    focus: {
      border: colorPrimary.toString(),
    },
    placeholder: {
      color: colorEmphasisLow.toString(),
    },
  },
  link: {
    color: colorPrimary.toString(),
    hover: {
      color: colorPrimary.darken(0.2).toString(),
    },
  },
  menuButton: {
    background: colorBackground.toString(),
    border: colorElevation3.toString(),
    item: {
      disabled: {
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorElevation3.toString(),
        color: colorEmphasisHigh.toString(),
      },
    },
    separator: {
      color: colorElevation3.toString(),
    },
  },
  notification: {
    background: colorElevation0.toString(),
    caption: {
      color: colorEmphasisHigh.toString(),
    },
    color: colorEmphasisMedium.toString(),
  },
  placeholder: {
    base: colorElevation2.toString(),
    highlight: colorElevation0.toString(),
  },
  radius: {
    tiny: '0.15rem',
    small: '0.25rem',
    medium: '0.5rem',
    large: '0.8rem',
  },
  scrollbar: {
    thumb: {
      background: colorElevation9.toString(),
      hover: colorElevation9.darken(0.3).toString(),
      size: 12,
    },
    track: {
      background: colorElevation2.toString(),
      spacing: 2,
    },
  },
  select: {
    background: colorElevation0.toString(),
    border: colorElevation8.toString(),
    color: colorEmphasisHigh.toString(),
    focus: {
      border: colorPrimary.toString(),
    },
    icon: colorEmphasisLow.toString(),
    option: {
      background: colorElevation0.toString(),
      border: colorElevation3.toString(),
      selected: {
        background: colorElevation3.toString(),
      },
    },
    placeholder: {
      color: colorEmphasisLow.toString(),
    },
  },
  selectList: {
    option: {
      selected: {
        background: colorPrimary.toString(),
        color: colorInverted.toString(),
      },
    },
  },
  selection: {
    background: colorPrimary.toString(),
    color: colorInverted.toString(),
  },
  severity: {
    info: colorInfo.toString(),
    negative: colorNegative.toString(),
    none: colorEmphasisLow.toString(),
    positive: colorPositive.toString(),
    primary: colorPrimary.toString(),
    warning: colorWarning.toString(),
  },
  shadow: {
    highlight: [
      '0 0 0.3px rgba(0, 0, 0, 0.016)',
      '0 0 0.9px rgba(0, 0, 0, 0.022)',
      '0 0 1.8px rgba(0, 0, 0, 0.028)',
      '0 0 3.7px rgba(0, 0, 0, 0.034)',
      '0 0 10px rgba(0, 0, 0, 0.05)',
    ].join(','),
    small: [
      '0 0.2px 0.4px rgba(0, 0, 0, 0.016)',
      '0 0.4px 1px rgba(0, 0, 0, 0.022)',
      '0 0.9px 2.1px rgba(0, 0, 0, 0.028)',
      '0 1.8px 4.4px rgba(0, 0, 0, 0.034)',
      '0 5px 12px rgba(0, 0, 0, 0.05)',
    ].join(','),
    medium: [
      '0 0.3px 0.6px rgba(0, 0, 0, 0.016)',
      '0 0.9px 1.6px rgba(0, 0, 0, 0.022)',
      '0 1.8px 3.2px rgba(0, 0, 0, 0.028)',
      '0 3.7px 6.6px rgba(0, 0, 0, 0.034)',
      '0 10px 18px rgba(0, 0, 0, 0.05)',
    ].join(','),
    large: [
      '0 0.3px 0.4px rgba(0, 0, 0, 0.025)',
      '0 0.7px 1px rgba(0, 0, 0, 0.035)',
      '0 1.4px 2.1px rgba(0, 0, 0, 0.045)',
      '0 2.9px 4.4px rgba(0, 0, 0, 0.055)',
      '0 8px 12px rgba(0, 0, 0, 0.08)',
    ].join(','),
    huge: [
      '0 0 0.8px rgba(0, 0, 0, 0.047)',
      '0 0 2.1px rgba(0, 0, 0, 0.067)',
      '0 0 4.3px rgba(0, 0, 0, 0.083)',
      '0 0 8.8px rgba(0, 0, 0, 0.103)',
      '0 0 24px rgba(0, 0, 0, 0.15)',
    ].join(','),
  },
  spacing: {
    absolute: {
      tiny: '0.25rem',
      small: '0.5rem',
      smaller: '0.75rem',
      medium: '1rem',
      larger: '1.25rem',
      large: '1.5rem',
      huge: '2rem',
    },
    relative: {
      tiny: '0.25em',
      small: '0.5em',
      smaller: '0.75em',
      medium: '1em',
      larger: '1.25em',
      large: '1.5em',
      huge: '2em',
    },
  },
  table: {
    border: colorElevation2.toString(),
    details: {
      background: colorElevation0.toString(),
    },
    header: {
      color: colorEmphasisLow.toString(),
    },
    highlight: {
      background: colorPrimary.alpha(0.15).toString(),
    },
    hover: {
      background: colorElevation3.toString(),
      color: colorEmphasisHigh.toString(),
    },
    select: {
      background: colorPrimary.toString(),
      color: colorInverted.toString(),
    },
  },
  timeline: {
    icon: {
      background: colorPositive.toString(),
      color: colorInverted.toString(),
    },
    line: {
      color: colorElevation5.toString(),
    },
    time: {
      color: colorEmphasisLow.toString(),
    },
  },
  transaction: {
    action: {
      archive: colorTxIgnore.toString(),
      disabled: {
        color: colorEmphasisLow.toString(),
      },
      hover: {
        background: colorElevation3.toString(),
      },
      match: colorTxMatch,
      restore: colorTxRestore.toString(),
      select: colorTxDeselect.toString(),
      unmatch: colorTxUnmatch.toString(),
    },
    bank: {
      color: colorTxBank,
    },
    error: colorNegative,
    info: {
      highlight: colorTxHighlight.toString(),
    },
    label: {
      color: colorInverted.toString(),
    },
    match: {
      color: colorTxState,
    },
    status: {
      archived: colorTxIgnored.toString(),
      matched: colorTxMatched.toString(),
      unmatched: colorTxUnmatched.toString(),
    },
    success: colorTxSuccess,
    system: {
      color: colorTxSystem,
    },
  },
  transition: {
    fast: '100ms',
    medium: '150ms',
    slow: '300ms',
  },
  typography: {
    emphasis: {
      high: colorEmphasisHigh.toString(),
      medium: colorEmphasisMedium.toString(),
      low: colorEmphasisLow.toString(),
    },
  },
};
